import { sanitizeText } from './security';
import { handleRateLimit } from '../middleware/rateLimit';
import { supabase } from './supabase';

/**
 * Submits form data to a Google Sheet using the Google Apps Script Web App
 * and backs up the data to Supabase
 * @param data Form data to submit
 * @returns Promise that resolves with the response
 */
export const submitToGoogleSheets = async (data: any): Promise<any> => {
  try {
    // Get the Google Sheets URL from environment variables
    const scriptUrl = import.meta.env.VITE_GOOGLE_SHEETS_URL;
    
    if (!scriptUrl) {
      console.error('Google Sheets URL not configured in environment variables');
      throw new Error('Form submission service not configured');
    }

    // Check if we're in development mode and use a fallback for testing
    if (import.meta.env.DEV) {
      console.log('DEV MODE: Simulating successful form submission');
      // Simulate a delay to mimic network request
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Store in Supabase even in dev mode
      await backupToSupabase(data);
      
      // Simulate a successful response in development
      return {
        success: true,
        message: 'Form submitted successfully (DEV MODE)'
      };
    }

    // Sanitize text fields
    const sanitizedData = {
      ...data,
      firstName: sanitizeText(data.firstName),
      lastName: sanitizeText(data.lastName),
      email: sanitizeText(data.email),
      message: sanitizeText(data.message),
      company: sanitizeText(data.company || ''),
      otherSpecialty: sanitizeText(data.otherSpecialty || '')
    };

    console.log('Submitting form data to:', scriptUrl);
    console.log('Form data:', JSON.stringify(sanitizedData));

    // First, backup to Supabase regardless of Google Sheets success
    await backupToSupabase(sanitizedData);

    // Make the request to Google Sheets with timeout
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 30000); // 30 second timeout

    try {
      // Make the request with proper CORS headers
      const response = await fetch(scriptUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Origin': window.location.origin
        },
        body: JSON.stringify(sanitizedData),
        mode: 'cors',
        signal: controller.signal,
        credentials: 'omit' // Don't send cookies
      });

      clearTimeout(timeoutId);

      // Log the raw response for debugging
      console.log('Raw response status:', response.status);
      console.log('Raw response headers:', Object.fromEntries([...response.headers.entries()]));
      
      // Get the response text first
      const responseText = await response.text();
      console.log('Response text:', responseText);
      
      // Check if response is ok
      if (!response.ok) {
        console.error('HTTP error response:', response.status, response.statusText);
        throw new Error(`HTTP error! status: ${response.status}, response: ${responseText}`);
      }

      // Try to parse as JSON
      let result;
      try {
        result = JSON.parse(responseText);
        console.log('Parsed response:', result);
      } catch (parseError) {
        console.error('Error parsing JSON response:', parseError);
        
        // If we can't parse JSON but got a 200 OK, assume success
        if (response.ok) {
          return {
            success: true,
            message: 'Form submitted successfully (non-JSON response)'
          };
        }
        
        throw new Error('Invalid response format from server');
      }

      if (!result.success) {
        throw new Error(result.message || 'Form submission failed');
      }

      // Send email notifications directly from the client
      await sendEmailNotifications(sanitizedData);

      return {
        success: true,
        message: 'Form submitted successfully'
      };
    } catch (fetchError: any) {
      clearTimeout(timeoutId);
      
      if (fetchError.name === 'AbortError') {
        throw new Error('Request timed out. Please try again later.');
      }
      
      throw fetchError;
    }
  } catch (error: any) {
    console.error('Form submission error:', error);
    
    // Return a more descriptive error message
    let errorMessage = 'Form submission failed';
    
    if (error instanceof Error) {
      if (error.message.includes('Failed to fetch') || error.name === 'TypeError') {
        errorMessage = 'Unable to reach the form submission service. Please check your internet connection and try again.';
      } else if (error.message.includes('NetworkError')) {
        errorMessage = 'Network error occurred. Please check your connection and try again.';
      } else if (error.message.includes('timeout')) {
        errorMessage = 'Request timed out. Please try again.';
      } else {
        errorMessage = `Form submission failed: ${error.message}`;
      }
    }
    
    return {
      success: false,
      message: errorMessage
    };
  }
};

/**
 * Backs up form submission data to Supabase
 * @param data Form data to backup
 */
async function backupToSupabase(data: any) {
  try {
    console.log('Backing up form data to Supabase');
    
    // Prepare data for Supabase
    const formData = {
      first_name: data.firstName,
      last_name: data.lastName,
      email: data.email,
      phone: data.phone,
      company: data.company,
      specialty: data.specialty === 'other' ? data.otherSpecialty : data.specialty,
      practice_size: data.practiceSize,
      budget: data.budget,
      message: data.message,
      agreed_to_terms: data.agreeToTerms,
      source: 'contact_form',
      lead_status: 'new'
    };
    
    // Insert into contacts table
    const { data: contact, error } = await supabase
      .from('contacts')
      .insert([formData])
      .select();
    
    if (error) {
      console.error('Error backing up to Supabase:', error);
      throw error;
    }
    
    console.log('Form data backed up to Supabase successfully:', contact);
    
    // Log the interaction
    if (contact && contact.length > 0) {
      const contactId = contact[0].id;
      
      await supabase
        .from('interactions')
        .insert([{
          contact_id: contactId,
          type: 'form_submission',
          channel: 'website',
          description: 'Contact form submission'
        }]);
    }
    
    return true;
  } catch (error) {
    console.error('Supabase backup error:', error);
    // Don't throw error here, we want the Google Sheets submission to continue
    return false;
  }
}

/**
 * Sends email notifications about the form submission
 * @param data Form data
 */
async function sendEmailNotifications(data: any) {
  try {
    // Use Supabase Edge Function to send email notifications
    const { error } = await supabase.functions.invoke('send-admin-notification', {
      body: {
        emails: [
          'victoria@purneeds.com',
          'leads@grokly.me',
          'leads@groklymed.me',
          'grokly.me@gmail.com',
          'contactformsubmission@groklymed.me'
        ],
        formData: data,
        subject: `New Contact Form Submission from ${data.firstName} ${data.lastName}`,
        note: 'A new contact form submission has been received.'
      }
    });
    
    if (error) {
      console.error('Error sending email notifications:', error);
      throw error;
    }
    
    // Send confirmation email to the user
    await supabase.functions.invoke('send-confirmation-email', {
      body: {
        to: data.email,
        firstName: data.firstName,
        specialty: data.specialty === 'other' ? data.otherSpecialty : data.specialty,
        submissionTime: new Date().toISOString(),
        subject: 'Thank You for Your Inquiry',
        note: 'We have received your inquiry and will be in touch soon.'
      }
    });
    
    return true;
  } catch (error) {
    console.error('Email notification error:', error);
    // Don't throw error here, we want to return success even if email fails
    return false;
  }
}